import { useState } from "react";
import { Download } from "lucide-react";
import { Button, Card, FormSection, useToast } from "../../components/ui";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { ApiError, downloadFile } from "../../lib/api";

/**
 * Data export, and an HONEST statement of what backup does and does not exist.
 *
 * A full backup/restore system (scheduling, retention, integrity verification,
 * owner-only restore) is not built. Rather than show a Backups screen with
 * dead controls, this says plainly what is available today and what protects
 * the data underneath — Cloudflare's own D1 durability — without claiming a
 * Time Travel capability that has not been verified on this account.
 */
export function DataSettingsSection() {
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";
  const [exporting, setExporting] = useState(false);

  const canExport = hasPermission("settings.export");

  const runExport = async () => {
    setExporting(true);
    try {
      await downloadFile("/settings/export", `laundrypro-export-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (err) {
      showToast("error", err instanceof ApiError ? err.message : ar ? "تعذّر التصدير." : "Couldn't export.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <FormSection legend={ar ? "تصدير البيانات" : "Data export"}>
          <p className="mb-3 text-sm text-muted-foreground">
            {ar
              ? "نسخة كاملة من بيانات المنشأة بصيغة JSON — للأرشفة أو النقل. لا تتضمن كلمات المرور أو رموز الجلسات."
              : "A complete JSON copy of the business's data — for archiving or portability. Never includes passwords or session tokens."}
          </p>
          {canExport ? (
            <Button variant="outline" icon={<Download className="size-4" />} loading={exporting} onClick={runExport}>
              {ar ? "تنزيل نسخة كاملة" : "Download full export"}
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              {ar ? "يتطلب صلاحية تصدير البيانات." : "Requires the data-export permission."}
            </p>
          )}
        </FormSection>
      </Card>

      <Card>
        <FormSection legend={ar ? "النسخ الاحتياطي" : "Backups"}>
          <p className="text-sm text-ink-700">
            {ar
              ? "لا يوجد حتى الآن نظام نسخ احتياطي مجدول داخل التطبيق."
              : "There is no scheduled in-app backup system yet."}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {ar
              ? "قاعدة البيانات تعمل على Cloudflare D1 وتعتمد على متانتها وآليات الاستعادة على مستوى المنصة. التصدير أعلاه هو النسخة اليدوية المتاحة اليوم. لن نعرض هنا قدرات لم يجرِ التحقق منها فعلياً."
              : "The database runs on Cloudflare D1 and relies on the platform's own durability and recovery. The export above is the manual copy available today. Capabilities that have not actually been verified are not shown here."}
          </p>
        </FormSection>
      </Card>
    </div>
  );
}
